import { useEffect, useRef, type ReactNode } from 'react';
import { S, t, type Leaf, type Locale } from '../../i18n/strings';
import styles from './aqua.module.css';

interface Props {
  readonly title: Leaf;
  readonly body: Leaf;
  readonly lang: Locale;
  readonly onClose: () => void;
}

/**
 * La boîte de dialogue du bac : une fenêtre d'AquariumOS, en plus petit.
 *
 * C'est là que répondent les commandes qui ne peuvent qu'échouer — créer un
 * second poisson, exporter Steve, éteindre le bac. Un menu qui ne fait rien
 * n'est pas une blague ; un menu qui répond « le bac ne contient qu'un poisson
 * et il est déjà là » en est une.
 *
 * Le focus part sur le bouton et `Échap` referme : une modale qu'on ne peut pas
 * fermer au clavier est un piège, même pour une blague.
 */
export function AquaDialog({ title, body, lang, onClose }: Props): ReactNode {
  const okRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    okRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      className={styles.dialogBackdrop}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label={t(title, lang)}>
        <h2 className={styles.titlebar}>
          <span>{t(title, lang)}</span>
          <span className={styles.dots}>
            <button
              type="button"
              className={`${styles.dot} ${styles.dotOrange}`}
              aria-label={t(S.aqua.dots.close, lang)}
              onClick={onClose}
            />
          </span>
        </h2>
        <div className={styles.dialogBody}>
          <p className={styles.dialogText}>{t(body, lang)}</p>
          <button ref={okRef} type="button" className={styles.button} onClick={onClose}>
            {t(S.aqua.dialogs.ok, lang)}
          </button>
        </div>
      </div>
    </div>
  );
}
